import { createContext, useContext } from 'react';
import type { User } from '@/types';

// Types shared across match detail tabs

export interface Match {
  id: number;
  tournament_id?: number;
  team1: string;
  team2: string;
  team1_short?: string;
  team2_short?: string;
  match_date: string;
  status: string;
  score_team1?: number;
  score_team2?: number;
  team1_win_coefficient?: number | null;
  team2_win_coefficient?: number | null;
  draw_coefficient?: number | null;
  /**
   * Live timing — see TournamentDetailLayout/context.tsx `Match` for the full
   * contract. Populated by the live-polling job for in-play matches.
   */
  elapsed_minutes?: number | null;
  elapsed_extra_minutes?: number | null;
  elapsed_updated_at?: string | null;
  tournament_name?: string;
  team1_logo_url?: string;
  team2_logo_url?: string;
  coefficients_source?: string;
  team1_bookmaker?: string | null;
  draw_bookmaker?: string | null;
  team2_bookmaker?: string | null;
  user_prediction?: {
    predicted_score_team1: number;
    predicted_score_team2: number;
    predicted_outcome?: string;
    odds_score?: number | string | null;
    points_earned?: number;
    points_breakdown?: any;
  };
}

export interface LocalTournament {
  id: number;
  name: string;
  description?: string;
  isPublic?: boolean;
  is_public?: boolean;
  manualCoefficients?: boolean;
  includeQualificationStages?: boolean;
  predictionDeadlineBufferMinutes?: number;
  user_role?: 'holder' | 'maintainer' | 'admin' | 'participant';
  userRole?: 'holder' | 'maintainer' | 'admin' | 'participant';
  userStatus?: 'active' | 'blocked' | 'awaiting';
  userBlockReason?: string | null;
  leagueLogo?: string;
  leagueName?: string;
  league?: {
    name: string;
    logo_url?: string;
  };
  /** ISO timestamp when the tournament was archived; null/undefined while active. */
  archivedAt?: string | null;
}

export interface Prediction {
  user_id: number;
  username: string;
  first_name: string;
  last_name: string;
  predicted_score_team1: number;
  predicted_score_team2: number;
  odds_score?: number | string | null;
  points_breakdown?: {
    score: number;
    result: number;
    total: number;
  };
  total_points?: number;
}

export interface Coefficients {
  source: string;
  team1_win_coefficient?: number | null;
  draw_coefficient?: number | null;
  team2_win_coefficient?: number | null;
}

export interface MatchDetailContextType {
  // Core data
  tournament: LocalTournament;
  match: Match;
  tournamentId: string;
  matchId: string;

  // User data
  user: User | null;
  userPrediction: any;

  // Predictions data
  predictions: Prediction[];
  coefficients: Coefficients | undefined;
  predictionsLoading: boolean;
  predictionsError: any;
  /** True while other participants' predictions are hidden (deadline not yet reached). */
  predictionsLocked: boolean;
  /** ISO timestamp when other participants' predictions become visible. */
  predictionsLockTime: string | null;

  // Debug mode
  debugMode: any;

  // Permissions
  canEditPrediction: boolean;
  canEditCoefficients: boolean;
  /** True when the tournament is archived — predictions/coefficients are read-only everywhere. */
  isArchived: boolean;

  // Form state
  showPredictionForm: boolean;
  setShowPredictionForm: (show: boolean) => void;
  showCoefficientForm: boolean;
  setShowCoefficientForm: (show: boolean) => void;

  // Actions
  refetchPredictions: () => Promise<void>;
  refetchMatch: () => void;

  // Layout helpers
  isMobile: boolean;
  /**
   * True when the page was opened from outside the tournament (the standalone
   * match page): back returns there, so the tabs offer a link to the
   * tournament's match list.
   */
  openedOutsideTournament: boolean;
}

export const MatchDetailContext = createContext<MatchDetailContextType | undefined>(undefined);

export function useMatchDetailContext() {
  const ctx = useContext(MatchDetailContext);
  if (!ctx) throw new Error('useMatchDetailContext must be used within MatchDetailLayout');
  return ctx;
}
